import React, { useEffect, useMemo, useRef } from 'react';
import { clone } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { useGLTF, useTexture } from '@react-three/drei';
import { MeshPhysicalMaterial, MeshStandardMaterial, LineBasicMaterial, Vector2 } from 'three';
import { SRGBColorSpace, LinearSRGBColorSpace } from 'three';

const _ = require('lodash');

function LegacyAvatar({
  avatar_url, 
  setMorphTargetDictionaryBody, 
  setMorphTargetDictionaryLowerTeeth, 
  setAvatarRef, 
  mixer,
  materialConfig,
}) {
  const gltf = useGLTF(avatar_url);
  const materialsRef = useRef({});

  useEffect(() => {
    if (gltf.scene && setAvatarRef) {
      setAvatarRef(gltf.scene);
    }
  }, [gltf.scene, setAvatarRef]);

  const [
    bodyTexture,
    eyesTexture,
    teethTexture,
    bodySpecularTexture,
    bodyRoughnessTexture,
    bodyNormalTexture,
    teethNormalTexture,
    hairTexture,
    tshirtDiffuseTexture,
    tshirtNormalTexture,
    tshirtRoughnessTexture,
    hairAlphaTexture,
    hairNormalTexture,
    hairRoughnessTexture,
  ] = useTexture([
    "/images/body.webp",
    "/images/eyes.webp",
    "/images/teeth_diffuse.webp",
    "/images/body_specular.webp",
    "/images/body_roughness.webp",
    "/images/body_normal.webp",
    "/images/teeth_normal.webp",
    "/images/h_color.webp",
    "/images/tshirt_diffuse.webp",
    "/images/tshirt_normal.webp",
    "/images/tshirt_roughness.webp",
    "/images/h_alpha.webp",
    "/images/h_normal.webp",
    "/images/h_roughness.webp",
  ]);

  _.each([
    bodyTexture,
    eyesTexture,
    teethTexture,
    teethNormalTexture,
    bodySpecularTexture,
    bodyRoughnessTexture,
    bodyNormalTexture,
    tshirtDiffuseTexture,
    tshirtNormalTexture,
    tshirtRoughnessTexture,
    hairAlphaTexture,
    hairNormalTexture,
    hairRoughnessTexture
  ], t => {
    if (t) {
      t.colorSpace = SRGBColorSpace;
      t.flipY = false;
    }
  });

  if (bodyNormalTexture) bodyNormalTexture.colorSpace = LinearSRGBColorSpace;
  if (tshirtNormalTexture) tshirtNormalTexture.colorSpace = LinearSRGBColorSpace;
  if (teethNormalTexture) teethNormalTexture.colorSpace = LinearSRGBColorSpace;
  if (hairNormalTexture) hairNormalTexture.colorSpace = LinearSRGBColorSpace;

  const mc = materialConfig || {};

  gltf.scene.traverse(node => {
    if (node.type === 'Mesh' || node.type === 'LineSegments' || node.type === 'SkinnedMesh') {
      node.castShadow = true;
      node.receiveShadow = true;
      node.frustumCulled = false;

      let matName = null;
      let matType = null;
      let matTexture = null;
      let matAlphaMap = null;
      let matRoughnessMap = null;
      let matNormalMap = null;
      let matSpecularMap = null;
      let matSide = null;
      let matTransparent = null;
      let matDepthWrite = null;
      let defaultColor = null;
      let defaultRoughness = null;
      let defaultEnvIntensity = null;

      if (node.name.includes("Body")) {
        matName = 'body';
        matType = 'physical';
        matTexture = bodyTexture;
        matRoughnessMap = bodyRoughnessTexture;
        matNormalMap = bodyNormalTexture;
        defaultColor = 0xffffff;
        defaultRoughness = 1.7;
        defaultEnvIntensity = 0.8;
      } else if (node.name.includes("Eyes")) {
        matName = 'eyes';
        matType = 'standard';
        matTexture = eyesTexture;
        defaultColor = 0xffffff;
        defaultRoughness = 0.1;
        defaultEnvIntensity = 0.5;
      } else if (node.name.includes("Brows")) {
        node.material = new LineBasicMaterial({ color: 0x000000 });
        node.material.linewidth = 1;
        node.material.opacity = 0.5;
        node.material.transparent = true;
        node.visible = false;
        return;
      } else if (node.name.includes("Teeth")) {
        matName = 'teeth';
        matType = 'standard';
        matTexture = teethTexture;
        matNormalMap = teethNormalTexture;
        defaultColor = 0xffffff;
        defaultRoughness = 0.1;
        defaultEnvIntensity = 0.7;
      } else if (node.name.includes("Hair")) {
        matName = 'hair';
        matType = 'standard';
        matTexture = hairTexture;
        matAlphaMap = hairAlphaTexture;
        matNormalMap = hairNormalTexture;
        matRoughnessMap = hairRoughnessTexture;
        matSide = 2;
        matTransparent = true;
        matDepthWrite = false;
        defaultColor = 0x000000;
        defaultRoughness = 0.5;
        defaultEnvIntensity = 0.3;
      } else if (node.name.includes("TSHIRT")) {
        matName = 'tshirt';
        matType = 'standard';
        matTexture = tshirtDiffuseTexture;
        matRoughnessMap = tshirtRoughnessTexture;
        matNormalMap = tshirtNormalTexture;
        defaultColor = 0xffffff;
        defaultRoughness = 0.8;
        defaultEnvIntensity = 0.5;
      }

      if (matName) {
        const overrides = mc[matName] || {};

        if (matType === 'physical') {
          node.material = new MeshPhysicalMaterial();
        } else {
          node.material = new MeshStandardMaterial();
        }

        if (matTexture) node.material.map = matTexture;
        if (matAlphaMap) node.material.alphaMap = matAlphaMap;
        if (matRoughnessMap) node.material.roughnessMap = matRoughnessMap;
        if (matNormalMap) {
          node.material.normalMap = matNormalMap;
          node.material.normalScale = new Vector2(0.6, 0.6);
        }
        if (matSpecularMap) node.material.specularMap = matSpecularMap;
        if (matSide !== null) node.material.side = matSide;
        if (matTransparent !== null) node.material.transparent = matTransparent;
        if (matDepthWrite !== null) node.material.depthWrite = matDepthWrite;

        // Apply overrides
        node.material.roughness = overrides.roughness !== undefined ? overrides.roughness : defaultRoughness;
        node.material.metalness = overrides.metalness !== undefined ? overrides.metalness : 0;
        node.material.envMapIntensity = overrides.envMapIntensity !== undefined ? overrides.envMapIntensity : defaultEnvIntensity;
        if (overrides.color) {
          node.material.color.set(overrides.color);
        } else if (defaultColor !== null) {
          node.material.color.setHex(defaultColor);
        }

        materialsRef.current[matName] = node.material;
      }

      if (node.name.includes("Body") && setMorphTargetDictionaryBody && typeof setMorphTargetDictionaryBody === 'function') {
        setMorphTargetDictionaryBody(node.morphTargetDictionary);
      }
      if (node.name.includes("TeethLower") && setMorphTargetDictionaryLowerTeeth && typeof setMorphTargetDictionaryLowerTeeth === 'function') {
        setMorphTargetDictionaryLowerTeeth(node.morphTargetDictionary);
      }
    }
  });

  // Apply dynamic material updates when materialConfig changes
  useEffect(() => {
    if (!materialConfig) return;
    Object.entries(materialsRef.current).forEach(([matName, mat]) => {
      const overrides = materialConfig[matName];
      if (!mat || !overrides) return;
      if (overrides.roughness !== undefined) mat.roughness = overrides.roughness;
      if (overrides.metalness !== undefined) mat.metalness = overrides.metalness;
      if (overrides.envMapIntensity !== undefined) mat.envMapIntensity = overrides.envMapIntensity;
      if (overrides.color) mat.color.set(overrides.color);
    });
  }, [materialConfig]);

  return (
    <group name="avatar">
      <primitive object={gltf.scene} dispose={null} />
    </group>
  );
}

function NativeAvatar({ gltf, setAvatarRef, setMorphTargetDictionaryBody, setMorphTargetDictionaryLowerTeeth }) {
  const scene = useMemo(() => clone(gltf.scene), [gltf.scene]);
  useEffect(() => {
    scene.traverse(node => {
      if (!node.isMesh) return;
      node.frustumCulled = false;
      node.castShadow = true;
      node.receiveShadow = true;
      if (node.name.startsWith('Assistia_Body') && node.morphTargetDictionary) setMorphTargetDictionaryBody?.(node.morphTargetDictionary);
      if (node.name === 'Assistia_TeethLower') setMorphTargetDictionaryLowerTeeth?.(node.morphTargetDictionary);
    });
    setAvatarRef?.(scene);
    return () => setAvatarRef?.(null);
  }, [scene, setAvatarRef, setMorphTargetDictionaryBody, setMorphTargetDictionaryLowerTeeth]);
  return <primitive object={scene} dispose={null} />;
}

export default function Avatar(props) {
  const gltf = useGLTF(props.avatar_url);
  const embeddedMaterials = useMemo(() => {
    let found = false;
    gltf.scene.traverse(node => { if (node.userData.preserveMaterials) found = true; });
    return found;
  }, [gltf.scene]);
  return embeddedMaterials ? <NativeAvatar {...props} gltf={gltf} /> : <LegacyAvatar {...props} />;
}
